<script setup>
import {useI18n} from "vue-i18n";
import {onMounted, ref, watch} from "vue";
import {api} from "@/request.ts";

const { locale } = useI18n()

const news = ref()

const host = import.meta.env.VITE_HOST

onMounted(() => {
  loadNews()
})

const loadNews = async () => {
  const dataNews = (await api.get(`/${locale.value}/news/business/_jcr_content/root/full-body/bodylayoutcontainer_/tabordion/par0/case_list_it_copy_co.model.json?t=${new Date().getTime()}`)).data
  news.value = dataNews.items?.slice(0, 3);
}

watch(locale, () => {
  loadNews()
})
</script>

<template>
  <section class="news-section">
    <div class="news-section__inner">
      <div class="news-section__header">
        <h2 class="news-section__title">
          {{$t('Новости и обновления')}}
        </h2>

        <a :href="`${host}/${locale}/news`" class="news-section__all">
          {{$t('Все новости')}}
        </a>
      </div>
      <div class="news-section__scroller">
        <div class="news-section__grid">
          <a v-for="(item, index) in news" :href="item.page.path" :key="index" class="news-card-wrapper">
            <article class="news-card">
              <div class="news-card__image">
                <img :src="item.image" :alt="item.title">
              </div>

              <div class="news-card__content">
                <div class="news-card__category">
                  {{ item.category }}
                </div>

                <h3 class="news-card__title">
                  {{ item.title }}
                </h3>

                <div class="news-card__meta">
                  <span>{{ item.createDateString }}</span>
                </div>
              </div>
            </article>
          </a>
        </div>
      </div>
      <a :href="`${host}/${locale}/news`" class="news-section__all mobile-only">
        {{$t('Все новости')}}
      </a>
    </div>
  </section>
</template>

<style scoped lang="scss">
.news-section {
  width: 100%;
  background: #f4f5f7;
  padding: 0 0 80px;
  box-sizing: border-box;

  &__inner {
    max-width: 1280px;
    margin: 0 auto;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 32px;
  }

  &__title {
    margin: 0;
    font-size: 36px;
    font-weight: 600;
    color: $primary-dark;
  }

  &__all {
    color: #333;
    font-size: 16px;
    line-height: 1;
    text-decoration: none;
    padding: 6px 14px;
    border-radius: 8px;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: $color-primary;
    }
    &.mobile-only {
      display: none;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
}
.news-card-wrapper {
  text-decoration: none;
}
.news-card {
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    box-shadow: 0 7px 16px 0 rgba(19, 32, 77, 0.07);

    .news-card__image img {
      transform: scale(1.04);
    }
  }

  &__image {
    width: calc(100% - 12px);
    height: 202px;
    border-radius: 16px;
    overflow: hidden;
    margin: 6px 6px 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      transition: transform 0.3s ease;

    }
  }

  &__content {
    padding: 16px 32px 32px;
  }

  &__category {
    margin-bottom: 16px;
    font-size: 16px;
    line-height: 1;
    font-weight: 500;
    color: $primary-blue;
    text-transform: uppercase;
  }

  &__title {
    margin: 0 0 24px;
    min-height: 50px;
    font-size: 18px;
    font-weight: 500;
    color: $primary-dark;
    white-space: pre-line;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 24px;
    color: #6C757D;
    font-size: 16px;
    line-height: 1;
    white-space: nowrap;
    justify-content: space-between;
  }

  &__meta-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;

    img {
      width: 16px;
      height: 16px;
      object-fit: contain;
    }
  }
}

@media (max-width: 1440px) {
  .news-section {
    padding: 0 80px 80px;
  }
}

@media (max-width: 1024px) {
  .news-section {
    padding: 0 64px 80px;
  }
}

@media (max-width: 700px) {
  .news-section {
    padding: 34px 0 36px;

    &__inner {
      max-width: none;
      margin: 0;
      overflow: hidden;
    }

    &__header {
      margin: 0 20px 16px;
    }

    &__title {
      margin: 0;
      font-size: 24px;
      line-height: 1.15;
      font-weight: 600;
    }

    &__scroller {
      width: 100%;
      overflow-x: auto;
      overflow-y: hidden;
      padding: 0 20px;
      box-sizing: border-box;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    &__grid {
      display: flex;
      gap: 8px;
      width: max-content;
      padding: 0;
    }

    &__all {
      display: none;
      &.mobile-only {
        display: block;
        width: fit-content;
        margin: 16px 20px 0;
        padding: 0;
        font-size: 15px;
        line-height: 1.2;
        color: #333;
        background: transparent;
      }
    }

  }

  .news-card {
    width: 230px;
    min-width: 230px;
    max-width: 230px;
    flex: 0 0 230px;
    border-radius: 12px;

    &__image {
      width: calc(100% - 12px);
      height: 147px;
      margin: 6px 6px 0;
      border-radius: 10px;
    }

    &__content {
      padding: 15px 15px 16px;
    }

    &__category {
      margin-bottom: 18px;
      font-size: 16px;
    }

    &__title {
      min-height: 44px;
      margin-bottom: 22px;
      font-size: 16px;
      line-height: 1.25;
      font-weight: 500;
    }

    &__meta {
      gap: 12px;
      font-size: 15px;
      flex-wrap: nowrap;
      overflow: hidden;
    }

    &__meta-item {
      gap: 5px;

      img {
        width: 15px;
        height: 15px;
      }

      &:last-child {
        display: none;
      }
    }
  }
}
</style>
